import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [receipt, setReceipt] = useState(null);
  const attempts = useRef(0);
  const { user } = useAuth();

  useEffect(() => {
    if (status !== "paid" || !user?.user_id) return;
    const storageKey = `linguasub-checkout-${user.user_id}`;
    try {
      const intent = JSON.parse(localStorage.getItem(storageKey));
      localStorage.setItem(`linguasub-confirmed-order-${user.user_id}`, sessionId);
      if (intent?.orderId === sessionId) localStorage.removeItem(storageKey);
    } catch { /* An invalid local draft does not change the saved server order. */ }
  }, [status, sessionId, user?.user_id]);

  useEffect(() => {
    attempts.current = 0;
    setReceipt(null);
    if (!sessionId) { setStatus("error"); return; }
    setStatus("polling");
    let timer;
    let active = true;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${encodeURIComponent(sessionId)}`);
        if (!active) return;
        setReceipt(data);
        if (data.payment_status === "paid" && data.fulfillment_status === "fulfilled") { setStatus("paid"); return; }
        if (data.verification_status === "rejected") { setStatus("review"); return; }
        if (["failed", "expired", "cancelled", "terminated"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (error) {
        if (!active) return;
        if (error?.response?.status === 401) { setStatus("signin"); return; }
        if (error?.response?.status === 409) { setStatus("review"); return; }
        if (error?.response?.status === 404) { setStatus("error"); return; }
      }
      if (!active) return;
      if (attempts.current >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white p-6 md:p-12" data-testid="payment-success-page">
      <div className="max-w-xl mx-auto pt-24 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-6" />
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mb-3" data-testid="payment-polling-title">
              Confirming your payment...
            </h1>
            <p className="text-sm text-zinc-400">Give us a moment — this usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-6" />
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-2">
              {receipt?.plan_name} purchase confirmed
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mb-3" data-testid="payment-success-title">
              You're all set.
            </h1>
            <p className="text-sm text-zinc-400 mb-8" data-testid="purchase-entitlement">
              {receipt?.currency?.toLowerCase() === "inr" && receipt?.amount_inr != null
                ? `Paid ₹${Number(receipt.amount_inr).toLocaleString("en-IN", { maximumFractionDigits: 2 })}. ` : ""}
              This purchase added {receipt?.minutes} minutes with {receipt?.duration_days}-day validity. See your dashboard for the current remaining balance and any approved adjustments. No automatic renewal; your existing balances are preserved.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              data-testid="payment-success-cta"
              className="h-11 px-6 bg-white text-zinc-950 hover:bg-zinc-200 rounded-none font-semibold transition-colors duration-200 inline-flex items-center gap-2"
            >
              Go to dashboard <ArrowRight className="w-4 h-4" />
            </Button>
          </>
        )}
        {["failed", "timeout", "error", "review", "signin"].includes(status) && (
          <>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mb-3" data-testid="payment-status-title">
              {status === "timeout" ? "Still confirming..." : status === "review" ? "Payment needs review" : status === "signin" ? "Sign in to confirm payment" : "Payment didn't complete"}
            </h1>
            <p className="text-sm text-zinc-400 mb-8" data-testid="payment-status-message">
              {status === "timeout"
                ? "Confirmation is taking longer than usual. Keep your order reference and contact support before paying again."
                : status === "review"
                  ? "We could not verify this purchase against the saved order. No new minutes have been added. Contact support with your order reference before paying again."
                  : status === "signin"
                    ? "Sign in with the account used for this purchase, then reopen this confirmation link."
                    : "Checkout was not confirmed. If your bank shows a debit, contact support before paying again. Your existing balance is unchanged."}
            </p>
            {sessionId && <p className="text-xs font-mono text-zinc-500 break-all mb-6" data-testid="payment-order-reference">Order: {sessionId}</p>}
            <div className="flex items-center justify-center gap-3">
              <Link to={status === "signin" ? "/" : "/support?tab=help"} data-testid="payment-help-link" className="h-11 px-6 border border-zinc-800 hover:bg-zinc-800 inline-flex items-center rounded-none text-sm">{status === "signin" ? "Sign in" : "Get help"}</Link>
              <Link to="/dashboard" data-testid="payment-dashboard-link" className="h-11 px-6 bg-white text-zinc-950 hover:bg-zinc-200 inline-flex items-center rounded-none text-sm font-semibold">Dashboard</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
